import { useState } from "react";
import { Plus } from "lucide-react";
import { useAssignmentOptions } from "../../backend/queries/users";
import { useCreateShift } from "../../backend/queries/workforce";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { ShiftTaskPicker } from "./ShiftTaskPicker";

const localDate = (value: Date) =>
  `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;

export function CreateShiftDialog({
  week,
  onCreated,
  initialAgentId = "",
  compact = false,
}: {
  week: Date;
  initialAgentId?: string;
  compact?: boolean;
  onCreated: (day: Date) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [agentId, setAgentId] = useState(initialAgentId);
  const [day, setDay] = useState(localDate(week));
  const [starts, setStarts] = useState("");
  const [ends, setEnds] = useState("");
  const [notes, setNotes] = useState("");
  const [tasks, setTasks] = useState<string[]>([]);
  const [error, setError] = useState("");
  const agents = useAssignmentOptions(
    { kind: "agent", limit: 50 },
    { enabled: open },
  );
  const create = useCreateShift();
  const overnight = Boolean(starts && ends && ends < starts);
  const submit = async (addAnother = false) => {
    setError("");
    if (
      !day ||
      !title.trim() ||
      !agentId ||
      !starts ||
      !ends ||
      starts === ends
    ) {
      setError(
        "Choose an agent, shift date, title, and different start and end times.",
      );
      return;
    }
    const start = new Date(`${day}T${starts}`);
    const end = new Date(`${day}T${ends}`);
    if (overnight) end.setDate(end.getDate() + 1);
    try {
      await create.mutateAsync({
        title: title.trim(),
        agent_id: agentId,
        starts_at: start.toISOString(),
        ends_at: end.toISOString(),
        notes: notes.trim() || null,
        tasks: tasks.map((task_id) => ({ task_id })),
      });
      onCreated(start);
      setOpen(addAnother);
      if (!addAnother) { setTitle(""); setAgentId(initialAgentId); }
      setStarts("");
      setEnds("");
      setNotes("");
      setTasks([]);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The shift could not be created. Try again.",
      );
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (value) { setDay(localDate(week)); if (initialAgentId) setAgentId(initialAgentId); }
        setOpen(value);
      }}
    >
      <DialogTrigger asChild>
        <Button variant={compact ? "ghost" : "default"} size={compact ? "sm" : "default"} aria-label={compact ? `Add shift on ${localDate(week)}` : undefined}>
          <Plus className="h-4 w-4" />
          {compact ? "Add shift" : "New shift"}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Allocate a shift</DialogTitle>
          <DialogDescription>
            Choose a roster day, set the hours, and select existing tasks.
            Drafts stay hidden until published.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label>Assignee</Label>
            <Select
              value={agentId}
              onValueChange={(value) => {
                setAgentId(value);
                setTasks([]);
              }}
            >
              <SelectTrigger aria-label="Assignee">
                <SelectValue placeholder="Select an agent" />
              </SelectTrigger>
              <SelectContent>
                {agents.data?.items
                  .filter((agent) => agent.role === "Agent")
                  .map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.firstname} {agent.lastname}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {agents.error && (
              <p role="alert">Could not load agents. Reopen to retry.</p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="shift-title">Shift title</Label>
            <Input
              id="shift-title"
              maxLength={160}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="shift-date">Shift date</Label>
            <Input
              id="shift-date"
              type="date"
              value={day}
              onChange={(event) => setDay(event.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="shift-start">Start time</Label>
              <Input
                id="shift-start"
                type="time"
                value={starts}
                onChange={(e) => setStarts(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="shift-end">End time</Label>
              <Input
                id="shift-end"
                type="time"
                value={ends}
                onChange={(e) => setEnds(e.target.value)}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Times in {Intl.DateTimeFormat().resolvedOptions().timeZone}.
            {overnight && " Ends the following day."}
          </p>
          <ShiftTaskPicker
            key={agentId}
            agentId={agentId}
            selected={tasks}
            onChange={setTasks}
            enabled={open}
          />
          <div className="grid gap-2">
            <Label htmlFor="shift-notes">Shift notes</Label>
            <Textarea
              id="shift-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant="outline" onClick={() => submit(true)} disabled={create.isPending}>Create and add another</Button>
          <Button onClick={() => submit()} disabled={create.isPending}>
            {create.isPending ? "Creating…" : "Create draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
